import { useCallback, useRef, useState } from 'react';
import { ApiError } from '../lib/api/client';
import { raisePanic } from '../lib/api/endpoints';
import { alertFromDto } from '../lib/api/mappers';
import type { AlertEvent } from '../types/alert';

// FE-17 — el envío del botón de pánico, sin la UI.
//
// La regla que gobierna todo este archivo: NUNCA decir "enviado" sin haber
// enviado. Cada estado sale de una respuesta real del backend, no de un
// setTimeout — ese es exactamente el defecto del issue #76 (el modal de
// "¿olvidaste tu contraseña?" que afirma haber enviado el correo sin llamar a
// `sendPasswordResetEmail`), y aquí el coste de repetirlo es que alguien
// accidentado crea que pidió ayuda.

export type PanicPhase = 'idle' | 'sending' | 'sent' | 'already_active' | 'failed';

export interface PanicFailure {
  detalle: string;
  reintentable: boolean;
}

const MAX_INTENTOS = 3;
// El primer intento sale ya; los reintentos esperan. No es cortesía con el
// servidor: es darle tiempo al túnel o al cambio de celda a terminar.
const BACKOFF_MS = [0, 2000, 5000];

const dormir = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Traduce el fallo a algo que un conductor pueda leer, y decide si insistir
 * sirve de algo. 400/403/404 NO se reintentan: repetir la misma petición nunca
 * los va a convertir en un 201, y gastar 7 segundos en descubrirlo es tiempo
 * que alguien no tiene.
 */
function describir(err: unknown): PanicFailure {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return { detalle: 'Tu usuario no puede declarar emergencias sobre este vehículo. Avisa por radio o teléfono.', reintentable: false };
    }
    if (err.status === 404) {
      // El contrato responde 404 —no 403— para un vehículo fuera de alcance:
      // la API no confirma su existencia. Para el conductor el efecto es el
      // mismo: este no es su vehículo de hoy.
      return { detalle: 'Este vehículo no está asignado a ti hoy. Avisa por radio o teléfono.', reintentable: false };
    }
    if (err.status >= 500) {
      return { detalle: 'El servidor no pudo registrar la emergencia.', reintentable: true };
    }
    return { detalle: err.detail ?? err.title, reintentable: false };
  }
  // Sin sesión, DNS, timeout, avión, túnel.
  return { detalle: 'Sin conexión con el servidor.', reintentable: true };
}

export interface PanicState {
  phase: PanicPhase;
  intento: number;
  maxIntentos: number;
  alert: AlertEvent | null;
  fallo: PanicFailure | null;
  enviar: () => void;
  descartar: () => void;
}

export function usePanicAlert(vehicleId: string | undefined, onAceptada?: (alerta: AlertEvent) => void): PanicState {
  const [phase, setPhase] = useState<PanicPhase>('idle');
  const [intento, setIntento] = useState(0);
  const [alert, setAlert] = useState<AlertEvent | null>(null);
  const [fallo, setFallo] = useState<PanicFailure | null>(null);
  // Con `useRef` y no con `phase`: el guardia tiene que ser síncrono, o dos
  // gestos seguidos lanzan dos peticiones antes del primer re-render.
  const enVuelo = useRef(false);

  const enviar = useCallback(() => {
    if (!vehicleId || enVuelo.current) return;
    enVuelo.current = true;
    setFallo(null);
    setAlert(null);

    void (async () => {
      try {
        for (let n = 1; n <= MAX_INTENTOS; n++) {
          setIntento(n);
          setPhase('sending');
          if (BACKOFF_MS[n - 1] > 0) await dormir(BACKOFF_MS[n - 1]);
          try {
            const { alert: dto, alreadyActive } = await raisePanic(vehicleId);
            const aceptada = alertFromDto(dto);
            setAlert(aceptada);
            setPhase(alreadyActive ? 'already_active' : 'sent');
            onAceptada?.(aceptada);
            return;
          } catch (err) {
            const info = describir(err);
            if (!info.reintentable || n === MAX_INTENTOS) {
              setFallo(info);
              setPhase('failed');
              return;
            }
            // Reintentar es seguro porque el endpoint es idempotente por
            // vehículo: si el intento anterior sí llegó y solo se perdió la
            // respuesta, este devuelve 200 con la MISMA alerta, nunca una
            // segunda. Sin esa garantía del contrato, un reintento automático
            // sobre un POST sería inaceptable.
          }
        }
      } finally {
        enVuelo.current = false;
      }
    })();
  }, [vehicleId, onAceptada]);

  const descartar = useCallback(() => {
    setPhase('idle');
    setIntento(0);
    setAlert(null);
    setFallo(null);
  }, []);

  return { phase, intento, maxIntentos: MAX_INTENTOS, alert, fallo, enviar, descartar };
}
